import { useMemo, useState } from "react";
import { Card, Form, Row } from "react-bootstrap";
import useLoadTestFlow from "../hooks/useLoadTestFlow.js";
import { analyticsTestFeedback } from "../utils/loadTestFeedback.js";
import {
    LoadTestFlowModal, LoadTestNotice, LoadTestBotCount, LoadTestSetupSummary,
    LoadTestProgress, LoadTestResult, LoadTestComparisonTable, LoadTestBadge,
    LoadTestMetricCard as MetricCard, loadTestElapsedLabel as elapsedLabel,
} from "./LoadTestFlow.js";
import {
    cancelAnalyticsUsageLoadTest,
    getAnalyticsUsageLoadTestReport,
    getAnalyticsUsageLoadTestStatus,
    startAnalyticsUsageLoadTest,
} from "../api/analyticsUsageLoadTest.js";

const DEFAULT_CONNECTION_COUNT = 100;
const MAX_CONNECTION_COUNT = 2000;
const DEFAULT_ANALYTICS_COVERAGE_PRESET = "balanced";
const PHASE_LABELS = Object.freeze({
    reserved: "Reserva criada",
    preparing: "Preparando conexões",
    running: "Executando jornadas",
    settling: "Consolidando métricas",
    reporting: "Gerando relatório",
    completed: "Concluído",
    cancelling: "Encerrando",
    cancelled: "Encerrado",
    failed: "Falhou",
});
const COMPARISON_LABELS = Object.freeze({
    session_starts: "Sessões iniciadas",
    session_finalized: "Sessões finalizadas",
    session_unfinished: "Sessões não finalizadas",
    guest_devices: "Dispositivos visitantes",
    foreground_connected_seconds: "Tempo conectado em primeiro plano",
    route_leaves: "Saídas de rota",
    route_duration_seconds: "Duração registrada nas rotas",
    route_accesses: "Acessos a rotas",
    daily_route_accesses: "Acessos à página por dia",
    daily_guest_sessions: "Sessões visitantes por dia",
    daily_guest_devices: "Dispositivos visitantes por dia",
    route_guest_sessions: "Sessões visitantes por rota",
    route_guest_devices: "Dispositivos visitantes por rota",
    daily_session_starts: "Sessões iniciadas no dia",
    session_overlaps: "Sessões sobrepostas no dia",
    checked_in_participants: "Participantes com check-in",
    checked_in_activated: "Participantes ativados com check-in",
});
const COMPARISON_GROUPS = Object.freeze([
    ["origins", "Origem das sessões"],
    ["daily_activity", "Atividade diária"],
    ["routes", "Páginas acessadas por dia"],
    ["session_health", "Saúde das sessões"],
]);
const COMPARISON_STATUS = Object.freeze({
    matched: { label: "Corresponde", variant: "primary" },
    mismatch: { label: "Divergente", variant: "primary" },
    pending: { label: "Provisório", variant: "secondary" },
    not_covered: { label: "Não coberto", variant: "info" },
    unavailable: { label: "Indisponível", variant: "secondary" },
    contaminated: { label: "Contaminado", variant: "danger" },
    context: { label: "Contexto", variant: "light" },
});
const SETTLEMENT_LABELS = Object.freeze({
    settled: "Persistência confirmada antes do prazo.",
    timed_out: "O prazo de consolidação terminou; a última observação está exibida abaixo.",
    unavailable: "A persistência não pôde ser observada para este resultado.",
    contaminated: "A observação não oferece um escopo confiável para reconciliação.",
    not_applicable: "A consolidação não se aplica a um runner não concluído ou sem ledger.",
});

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");
const valueOrDash = (value) => value === null || value === undefined ? "—" : formatNumber(value);
const expectedValue = (row) => {
    const range = row?.expected_range;
    if (!range || range.min === null || range.min === undefined || range.max === null || range.max === undefined) return valueOrDash(row?.expected);
    return `${valueOrDash(row?.expected)} (${formatNumber(range.min)}–${formatNumber(range.max)})`;
};
const comparisonDifference = (row) => valueOrDash(row?.difference ?? row?.delta);
const comparisonStatus = (status) => COMPARISON_STATUS[status] || { label: status || "Indisponível", variant: "secondary" };
const comparisonInterval = (row) => {
    const interval = row?.interval || {};
    if (interval.metric_date) return String(interval.metric_date) + (interval.timezone ? " · " + interval.timezone : "");
    if (!interval.start_at && !interval.end_at) return "—";
    const start = interval.start_at ? new Date(interval.start_at).toLocaleString("pt-BR") : "?";
    const end = interval.end_at ? new Date(interval.end_at).toLocaleString("pt-BR") : "?";
    return start + " → " + end;
};

const sessionKeyFor = (eventId) => `analytics-usage-load-test:${eventId}`;
const buildStoredUsageTestReport = (data) => {
    const job = data?.job || {};
    const report = data?.report || job.result || {};
    const webapp = report.expected_report_delta?.webapp || {};
    const comparison = data?.comparison || {};
    return {
        job: {
            id: job.id,
            status: job.status,
            progress: job.progress,
            phase: job.phase,
            phase_message: job.phase_message,
            created_at: job.created_at,
            started_at: job.started_at,
            finished_at: job.finished_at,
            input: job.input ? {
                connection_count: job.input.connection_count,
                event_timezone: job.input.event_timezone,
                pacing_mode: job.input.pacing_mode,
            } : undefined,
        },
        report: {
            partial: report.partial,
            passed: report.passed,
            run_id: report.run_id,
            report_scope: report.report_scope,
            webapp_analytics_coverage: report.webapp_analytics_coverage,
            validation: report.validation,
            checks: report.checks,
            failure: report.failure,
            started_at: report.started_at,
            finished_at: report.finished_at,
            warnings: report.warnings,
            expected_report_delta: {
                webapp: {
                    analytics_usage_summary: webapp.analytics_usage_summary,
                    analytics_sessions_started: webapp.analytics_sessions_started,
                    guest_devices: webapp.guest_devices,
                },
            },
        },
        dashboard_report: data?.dashboard_report ? {
            overview: data.dashboard_report.overview,
        } : null,
        comparison: data?.comparison ? {
            comparisons: comparison.comparisons,
            groups: comparison.groups,
            runner_verdict: comparison.runner_verdict,
            dashboard_verdict: comparison.dashboard_verdict,
        } : null,
        validation: data?.validation ? {
            runner: data.validation.runner,
            dashboard: data.validation.dashboard,
        } : null,
        settlement: data?.settlement ? {
            status: data.settlement.status,
            attempts: data.settlement.attempts,
        } : null,
    };
};

const attachLiveObservation = (nextJob, payload) => (
    payload?.observation ? { ...nextJob, analytics_observation: payload.observation } : nextJob
);

const ComparisonTable = ({ rows = [] }) => <LoadTestComparisonTable rows={rows.map((row) => {
    const status = comparisonStatus(row.status);
    return {
        id: [row.group, row.metric, row.key].join(":"),
        label: COMPARISON_LABELS[row.metric] || row.metric,
        metricKey: row.key,
        expected: expectedValue(row), observed: valueOrDash(row.observed), difference: comparisonDifference(row),
        status: status.label, variant: status.variant,
        tone: row.status === "mismatch" ? "table-warning" : row.status === "contaminated" ? "table-danger" : "",
        details: [["Fonte", row.source || "—"], ["Denominador", row.denominator || "—"], ["Período", comparisonInterval(row)]],
    };
})} />;

const ANALYTICS_SCENARIO_LABELS = Object.freeze({
    "participant_profile_open:networking_detail": "Perfil de participante · networking",
    "schedule_detail_open:schedule_detail": "Detalhe da agenda",
    "speaker_detail_open:speaker_detail": "Detalhe de palestrante",
    "exhibitor_detail_open:exhibitor_detail": "Detalhe de expositor",
    "sponsor_detail_open:sponsor_detail": "Detalhe de patrocinador",
    "exhibitor_impression:exhibitors": "Impressão · expositores",
    "sponsor_impression:sponsors_list": "Impressão · patrocinadores",
    "sponsor_link_click:default": "Clique no link do patrocinador",
    "exhibitor_impression:home": "Impressão · expositor na página inicial",
    "sponsor_impression:home": "Impressão · patrocinador na página inicial",
});

const analyticsCoverageRows = (coverage = {}) => {
    const requiredScenarios = Array.isArray(coverage.required_scenarios)
        ? coverage.required_scenarios : Object.keys(coverage.scenarios || {});
    return requiredScenarios.map((key) => {
        const scenario = coverage.scenarios?.[key];
        if (!scenario) return null;
        const unavailableSkips = Number(coverage.skipped_reasons?.[key + ":entity_unavailable"] || 0);
        const statusKey = scenario.status || (scenario.passed === true ? "passed"
            : Number(scenario.targeted_bots) > 0 && Number(scenario.eligible_bots) === 0
                && unavailableSkips >= Number(scenario.targeted_bots) ? "not_covered" : "failed");
        const status = statusKey === "passed" ? { label: "Coberto", variant: "primary" }
            : statusKey === "not_covered" ? { label: "Não coberto", variant: "info" }
                : { label: "Falhou", variant: "danger" };
        return {
            id: key,
            label: ANALYTICS_SCENARIO_LABELS[key] || key,
            metricKey: key,
            expected: statusKey === "not_covered" ? "Sem entidade aplicável"
                : "≥ " + formatNumber(coverage.threshold_percent ?? 99) + "% dos bots elegíveis",
            observed: formatNumber(scenario.accepted_bots) + "/" + formatNumber(scenario.eligible_bots) + " elegíveis",
            difference: Number(scenario.eligible_bots) > 0 ? formatNumber(scenario.coverage_percent) + "%" : "—",
            status: status.label,
            statusKey,
            variant: status.variant,
            tone: statusKey === "not_covered" ? "table-info" : statusKey === "failed" ? "table-danger" : "",
            details: [
                ["Bots alvo", formatNumber(scenario.targeted_bots)],
                ["Bots elegíveis", formatNumber(scenario.eligible_bots)],
                ["Motivo", statusKey === "not_covered" ? "O catálogo do evento não continha uma entidade elegível; o cenário não foi contado como correspondência."
                    : statusKey === "failed" ? "A cobertura ficou abaixo do limite exigido ou o cenário não foi exercitado." : "Cobertura confirmada."],
            ],
        };
    }).filter(Boolean);
};

export const AnalyticsUsageLoadTestModal = ({
    show,
    onHide,
    onTestRunningChange = () => {},
    onTerminal = () => {},
    context = {},
}) => {
    const eventId = Number(context.eventId);
    const sessionKey = useMemo(() => sessionKeyFor(eventId), [eventId]);
    const [connectionCount, setConnectionCount] = useState(String(DEFAULT_CONNECTION_COUNT));
    const [pacingMode, setPacingMode] = useState("realistic");
    const flow = useLoadTestFlow({
        show, sessionKey, enabled: Boolean(eventId),
        getStatus: (jobId) => getAnalyticsUsageLoadTestStatus({ jobId, eventId }),
        getReport: (jobId) => getAnalyticsUsageLoadTestReport({ jobId, eventId }),
        cancelJob: (jobId) => cancelAnalyticsUsageLoadTest({ jobId, eventId }),
        startJob: (input) => startAnalyticsUsageLoadTest({ eventId, ...input }),
        normalizeJob: (data) => attachLiveObservation(data?.job, data),
        compactReport: buildStoredUsageTestReport,
        getOutcome: analyticsTestFeedback,
        onTestRunningChange, onTerminal,
        onRestoreInputs: (nextJob) => {
            setConnectionCount(String(nextJob.input?.connection_count || connectionCount || DEFAULT_CONNECTION_COUNT));
            setPacingMode(nextJob.input?.pacing_mode || "realistic");
        },
    });
    const { job, finalData } = flow;
    const targetConnections = Number(job?.input?.connection_count || connectionCount || DEFAULT_CONNECTION_COUNT);
    const eventTimezone = job?.input?.event_timezone || "America/Sao_Paulo";
    const liveObservation = job?.analytics_observation || null;
    const liveAccepted = liveObservation?.accepted || job?.metadata?.analytics?.accepted || null;
    const liveDashboard = liveObservation?.dashboard?.overview || null;
    const liveObservationAgeSeconds = liveObservation?.captured_at
        ? Math.max(0, Math.floor((Date.now() - new Date(liveObservation.captured_at).getTime()) / 1000)) : null;
    const start = () => {
        const count = Number(connectionCount);
        if (!Number.isInteger(count) || count < 1 || count > MAX_CONNECTION_COUNT) {
            flow.setError("Informe um número inteiro entre 1 e 2.000 bots.");
            return;
        }
        flow.start({ connectionCount: count, coveragePreset: DEFAULT_ANALYTICS_COVERAGE_PRESET, pacingMode });
    };
    const reset = () => {
        if (!flow.reset()) return;
        setConnectionCount(String(DEFAULT_CONNECTION_COUNT));
        setPacingMode("realistic");
    };

    const result = finalData?.report || job?.result || {};
    const webapp = result?.expected_report_delta?.webapp || {};
    const dashboard = finalData?.dashboard_report?.overview || {};
    const comparisonData = finalData?.comparison || {};
    const comparison = comparisonData?.comparisons || [];
    const comparisonGroups = comparisonData?.groups || {};
    const checkInRows = comparisonGroups.checkin_context || comparison.filter((row) => row.group === "checkin_context");
    const dashboardVerdict = finalData?.validation?.dashboard || comparisonData?.dashboard_verdict || {};
    const settlement = finalData?.settlement || null;
    const usageSummary = webapp.analytics_usage_summary || {};
    const analyticsCoverage = result.webapp_analytics_coverage || {};
    const coverageRows = analyticsCoverageRows(analyticsCoverage);
    const reportJob = finalData?.job || job || {};
    const runId = result.run_id || result.report_scope?.run_id || "—";
    const reportStartedAt = reportJob.started_at || result.started_at || "—";
    const reportFinishedAt = reportJob.finished_at || result.finished_at || "—";
    const testOutcome = analyticsTestFeedback({ ...finalData, job: finalData?.job || job, report: result });

    return <LoadTestFlowModal
        show={show} onHide={onHide} flow={flow} testLabel="Dados analíticos" onStart={start} onReset={reset}
        cancelDescription="As conexões serão encerradas e o resultado parcial será consolidado. Os dados analíticos já criados serão mantidos."
        configuration={<>
            <LoadTestNotice>
                O relatório considera os acessos gerados pelos bots neste teste. Eles abrem o webapp, autenticam como participantes ou visitantes e executam jornadas de uso.
            </LoadTestNotice>
            <LoadTestBotCount value={connectionCount} onChange={setConnectionCount} maximum={MAX_CONNECTION_COUNT}>
                <Form.Text className="text-muted mt-2 d-block form-text">O teste termina quando as jornadas dos bots forem concluídas; o tempo decorrido será acompanhado na próxima etapa.</Form.Text>
            </LoadTestBotCount>
            <LoadTestSetupSummary items={[
                ["Evento", "#" + (eventId || "—")], ["Fuso horário", eventTimezone],
                ["Perfil", "Equilibrado"], ["Ritmo", pacingMode === "realistic" ? "Natural (Humano)" : "Rápido (Estresse)"],
                ["Bots configurados", valueOrDash(connectionCount)],
            ]} covered="sessões diretas, PWA, UTM e referência externa."
                excluded="QR, ações sem confirmação e tráfego de outros acessos." />
        </>}
        progress={<LoadTestProgress flow={flow} phaseLabels={PHASE_LABELS} targetConnections={targetConnections}>
            {liveObservation && <Card className="mb-3" border={liveObservation.status === "available" ? "info" : "secondary"}>
                <Card.Body className="py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="small">Comparação ao vivo provisória</strong>
                        <LoadTestBadge variant={comparisonStatus(liveObservation.status).variant}>{comparisonStatus(liveObservation.status).label}</LoadTestBadge>
                    </div>
                    <div className="small text-muted mb-2">
                        Última observação: {liveObservation.captured_at ? new Date(liveObservation.captured_at).toLocaleTimeString("pt-BR") : "aguardando"}{liveObservationAgeSeconds !== null ? " · há " + liveObservationAgeSeconds + "s" : ""}.
                        {liveObservation.reason ? " " + liveObservation.reason + "." : ""}
                    </div>
                    <Row className="g-2 mb-3">
                        <MetricCard title="Ações aceitas" value={valueOrDash(liveAccepted?.actions)} subtitle={"rotas: " + valueOrDash(liveAccepted?.route_accesses)} />
                        <MetricCard title="Sessões observadas" value={valueOrDash(liveDashboard?.session_starts)} subtitle="Escopo privado do job" />
                        <MetricCard title="Rotas observadas" value={valueOrDash(liveDashboard?.route_accesses)} subtitle="Leitura provisória" />
                    </Row>
                </Card.Body>
            </Card>}
        </LoadTestProgress>}
        results={finalData && <LoadTestResult outcome={testOutcome} partial={result.partial || testOutcome.cancelled} comparisonLabel={comparisonStatus(dashboardVerdict.status).label}>
            {settlement && <div className="small text-muted mb-3">
                <strong>Consolidação:</strong> {SETTLEMENT_LABELS[settlement.status] || "Estado de consolidação indisponível."}
                {Number.isFinite(Number(settlement.attempts)) ? " Tentativas: " + formatNumber(settlement.attempts) + "." : ""}
            </div>}
            <div className="small text-muted mb-3">
                <strong>Job:</strong> {reportJob.id || "—"} · <strong>Execução:</strong> {runId} · <strong>Período:</strong> {reportStartedAt} → {reportFinishedAt}
            </div>
            <Row className="g-2 mb-3">
                <MetricCard title="Sessões esperadas" value={valueOrDash(usageSummary.session_metrics?.starts ?? webapp.analytics_sessions_started)} subtitle={"observadas: " + valueOrDash(dashboard.session_starts)} />
                <MetricCard title="Dispositivos visitantes" value={valueOrDash(usageSummary.session_metrics?.guest_devices ?? webapp.guest_devices)} subtitle={"observados: " + valueOrDash(dashboard.guest_devices)} />
                <MetricCard title="Acessos a rotas" value={valueOrDash(dashboard.route_accesses)} subtitle="No escopo do job" />
                <MetricCard title="Tempo real do teste" value={elapsedLabel(job?.started_at || result.started_at, job?.finished_at || result.finished_at)} subtitle={formatNumber(job?.input?.connection_count) + " bots configurados"} />
            </Row>
            {coverageRows.length > 0 && <section className="mb-4">
                <h6>Jornadas analíticas</h6>
                {coverageRows.some((row) => row.statusKey === "not_covered") && <p className="small text-muted">
                    Cenários “Não coberto” não tinham uma entidade disponível no catálogo do evento. Eles não são tratados como correspondência; os cenários aplicáveis continuam sujeitos ao limite de cobertura.
                </p>}
                <LoadTestComparisonTable rows={coverageRows} />
            </section>}
            <h6 className="pt-2 mb-2">Comparação de dados</h6>
            {COMPARISON_GROUPS.map(([group, title]) => {
                const rows = comparisonGroups[group] || comparison.filter((row) => row.group === group);
                return <section key={group} className="mb-4">
                    <h6>{title}</h6>
                    <ComparisonTable rows={rows} />
                </section>;
            })}
            <Card className="mb-4" bg="light" border="0">
                <Card.Body>
                    <h6>Contexto de check-in</h6>
                    <ComparisonTable rows={checkInRows} />
                </Card.Body>
            </Card>
        </LoadTestResult>}
    />;
};
